interface TRPCErrorShape {
  message: string;
  code?: string;
  data?: {
    code?: string;
    zodError?: {
      issues?: ZodError[];
    };
  };
}

/**
 * Format tRPC/Zod validation errors into human-friendly messages
 * In development mode, includes technical details for debugging
 */
export const formatTrpcError = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'An unknown error occurred';
  }

  const isDevelopment = import.meta.env.DEV;

  // Check if this is a tRPC error with Zod validation issues
  const trpcError = error as TRPCErrorShape;
  if (trpcError.data?.zodError?.issues && Array.isArray(trpcError.data.zodError.issues)) {
    const userMessage = formatZodErrors(trpcError.data.zodError.issues);
    if (isDevelopment) {
      return `${userMessage}\n\nDeveloper details: ${JSON.stringify(trpcError.data.zodError.issues, null, 2)}`;
    }
    return userMessage;
  }

  const message = error.message;

  // Try to parse as JSON (Zod validation errors are often stringified JSON)
  try {
    const parsed = JSON.parse(message);
    
    // Handle array of Zod errors
    if (Array.isArray(parsed)) {
      const userMessage = formatZodErrors(parsed);
      if (isDevelopment) {
        return `${userMessage}\n\nDeveloper details: ${JSON.stringify(parsed, null, 2)}`;
      }
      return userMessage;
    }
    
    // Handle single Zod error object
    if (parsed.code && parsed.path && parsed.message) {
      const userMessage = formatZodError(parsed);
      if (isDevelopment) {
        return `${userMessage}\n\nDeveloper details: ${JSON.stringify(parsed, null, 2)}`;
      }
      return userMessage;
    }
  } catch {
    // Not JSON, continue with string parsing
  }

  // Check if message contains stringified JSON error
  const jsonMatch = message.match(/\[.*\]/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed)) {
        const userMessage = formatZodErrors(parsed);
        if (isDevelopment) {
          return `${userMessage}\n\nDeveloper details: ${JSON.stringify(parsed, null, 2)}`;
        }
        return userMessage;
      }
    } catch {
      // Continue with other parsing attempts
    }
  }

  // Extract validation errors from "Failed to update venue: [...]" format
  if (message.includes('Failed to update venue:')) {
    const errorPart = message.replace('Failed to update venue:', '').trim();
    try {
      const parsed = JSON.parse(errorPart);
      if (Array.isArray(parsed)) {
        const userMessage = formatZodErrors(parsed);
        if (isDevelopment) {
          return `${userMessage}\n\nDeveloper details: ${JSON.stringify(parsed, null, 2)}`;
        }
        return userMessage;
      }
    } catch {
      // Return the cleaned message
      return errorPart;
    }
  }

  // In development mode, always show both user-friendly and technical details
  if (isDevelopment) {
    // Check if this looks like a user-friendly message that might have technical details
    // Look for patterns like "Failed to create venue: [technical details]"
    const parts = message.split(/:\s+/);
    if (parts.length > 1 && parts[0].includes('Failed to')) {
      const userFriendly = parts[0];
      const technicalDetails = parts.slice(1).join(': ');
      return `${userFriendly}\n\n📋 Developer Details:\n${technicalDetails}`;
    }
    
    // For other errors, just show the message as-is in dev mode
    return message;
  }

  // In production, simplify long technical messages
  if (message.length > 200) {
    // Extract the first sentence or first 100 characters as the user-friendly message
    const firstSentence = message.split(/[.!?]/)[0];
    if (firstSentence && firstSentence.length < 150) {
      return firstSentence + '.';
    }
    return 'An error occurred while processing your request. Please check your input and try again.';
  }

  // In production, show the message as-is if it's reasonably short
  return message;
};

interface ZodError {
  code: string;
  expected?: string;
  received?: string | null;
  path: (string | number)[];
  message: string;
}

const formatZodError = (error: ZodError): string => {
  const fieldName = formatFieldPath(error.path);
  
  // Handle specific error types
  if (error.code === 'invalid_type') {
    if (error.received === 'null' || error.received === null) {
      return `${fieldName} is required`;
    }
    return `${fieldName} must be a ${error.expected}`;
  }
  
  // Use the message if it's already human-friendly
  if (error.message && !error.message.includes('Expected') && !error.message.includes('Received')) {
    return `${fieldName}: ${error.message}`;
  }
  
  return `${fieldName} is invalid`;
};

const formatZodErrors = (errors: ZodError[]): string => {
  if (errors.length === 0) {
    return 'Validation failed';
  }
  
  if (errors.length === 1) {
    return formatZodError(errors[0]);
  }
  
  // Multiple errors - format as a list
  const formattedErrors = errors.map(formatZodError);
  return `Please fix the following errors:\n${formattedErrors.map(e => `• ${e}`).join('\n')}`;
};

const formatFieldPath = (path: (string | number)[]): string => {
  if (path.length === 0) {
    return 'Field';
  }
  
  // Convert camelCase to Title Case with spaces
  const fieldName = path
    .map(segment => {
      if (typeof segment === 'number') {
        return `[${segment}]`;
      }
      // Convert camelCase to Title Case
      return segment
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    })
    .join(' ');
  
  return fieldName;
};
